import { CompRef } from '@wix/document-services-types';
import { ManifestComponentExtensionType } from '../stores';
import { AbstractExternalAPIHandler } from './AbstractExternalAPIHandler';
import { ChangeType } from '../ManifestChangeDetection';
import { CacheMethodResponse } from '../api/decorators/CacheMethodResponse';
import { MethodCacheStrategy } from '../api/MethodCacheStrategy';
import { DependencyToken } from '../dependencies';
import { APPLIED_PRESET_FIELD } from '../presets/constants';
import type { EditorPointer } from '@wix/editor-document-logic-api';

export class ExternalPresetsHandler extends AbstractExternalAPIHandler {
  /**
   * cache reason – OOI preset getter
   * works with compRef, so it ignores innerElementPath
   * means that we can share any OOI presets call for ROOT and its children
   */
  @CacheMethodResponse<
    [EditorPointer<CompRef>, ManifestComponentExtensionType]
  >({
    cacheStrategy: MethodCacheStrategy.PresetUpdate,
    buildCacheKey: (_self, editorPointer, extensionType) => {
      if (extensionType === ManifestComponentExtensionType.OOI) {
        const documentPointer =
          _self.editorPointerAPI!.getDocumentPointer(editorPointer);
        return _self.editorPointerAPI!.getEditorPointerUniqueId(
          documentPointer,
          undefined,
          { ignoreVariants: false },
        );
      }

      // probably, we should not cache for builder components, only OOI
      return editorPointer.asUniqueKey;
    },
    extractEditorPointers: (_self, editorPointer) => [editorPointer],
  })
  getAppliedPreset(
    editorPointer: EditorPointer<CompRef>,
    type: ManifestComponentExtensionType,
  ) {
    switch (type) {
      case ManifestComponentExtensionType.BUILDER: {
        const getPreset = this.dependenciesContributor.get(
          DependencyToken.BUILDER_PRESETS_GET_EFFECTIVE,
        );
        return getPreset(editorPointer)?.presetName ?? '';
      }

      case ManifestComponentExtensionType.OOI: {
        const getStyle = this.dependenciesContributor.get(
          DependencyToken.OOI_STYLE_GET_EFFECTIVE,
        );

        const style = getStyle(editorPointer);
        // @ts-expect-error DM typings are incorrect https://wix.atlassian.net/browse/EP-7087
        return style?.style?.strings?.[APPLIED_PRESET_FIELD] ?? '';
      }

      default:
        return null;
    }
  }

  getScopedAppliedPreset(
    editorPointer: EditorPointer<CompRef>,
    type: ManifestComponentExtensionType,
  ) {
    switch (type) {
      case ManifestComponentExtensionType.BUILDER: {
        const getPreset = this.dependenciesContributor.get(
          DependencyToken.BUILDER_PRESETS_GET,
        );
        return getPreset(editorPointer)?.presetName ?? '';
      }

      case ManifestComponentExtensionType.OOI: {
        const getStyle = this.dependenciesContributor.get(
          DependencyToken.OOI_STYLE_GET,
        );

        const style = getStyle(editorPointer);
        // @ts-expect-error DM typings are incorrect https://wix.atlassian.net/browse/EP-7087
        return style?.style?.strings?.[APPLIED_PRESET_FIELD] ?? '';
      }

      default:
        return null;
    }
  }

  applyPreset(
    editorPointer: EditorPointer<CompRef>,
    type: ManifestComponentExtensionType,
    presetName: string,
  ) {
    switch (type) {
      case ManifestComponentExtensionType.BUILDER: {
        const setPreset = this.dependenciesContributor.get(
          DependencyToken.BUILDER_PRESETS_UPDATE,
        );
        setPreset(editorPointer, presetName);

        break;
      }

      case ManifestComponentExtensionType.OOI: {
        const setStyle = this.dependenciesContributor.get(
          DependencyToken.OOI_STYLE_UPDATE,
        );
        setStyle(editorPointer, [
          {
            type: 'string',
            key: APPLIED_PRESET_FIELD,
            param: { value: presetName },
          },
        ]);

        break;
      }

      default:
        throw new Error('not implemented');
    }

    this.manifestChangeDetection.notify({
      type: ChangeType.Preset,
      editorPointer,
      extensionType: type,
      value: presetName,
    });
  }
}
